<template>
  <v-container class="pa-0" :class="{ 'export-embedded': embedded }" fluid>
    <v-container v-if="!embedded" class="pt-6 pb-6 px-4" fluid>
      <v-row align="center" class="ma-0">
        <v-col cols="auto" class="py-0 pr-2">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="Monat"
                v-model="monthFormatted"
                readonly
                outlined
                dense
                hide-details
                style="width: 150px;"
                color="#FF6600"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="month"
              no-title
              scrollable
              type="month"
              color="#FF6600"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="auto" class="py-0 pr-2">
          <v-select
            :items="groupingOptions"
            v-model="groupSelection"
            item-text="text"
            item-value="value"
            label="Gruppierung"
            outlined
            dense
            hide-details
            style="width: 280px;"
            color="#FF6600"
            @change="onGroupingChange"
          ></v-select>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-text-field
            v-model="searchText"
            label="Suche"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="width: 250px;"
            color="#FF6600"
            @input="onSearchInput"
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="collapseAllClick"
          >
            <v-icon small color="grey darken-2">{{ expanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="refreshDataGrid"
          >
            <v-icon small color="grey darken-2">mdi-refresh</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="exportToExcel"
          >
            <v-icon small color="grey darken-2">mdi-download</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-divider v-if="!embedded"></v-divider>
    <dx-data-grid
      :ref="gridRefName"
      :data-source="datasource"
      :columns="columns"
      :summary="summary"
      :show-borders="false"
      :show-column-lines="false"
      :column-auto-width="true"
      :allow-column-reordering="!gridTouch"
      :allow-column-resizing="!gridTouch"
      :hover-state-enabled="!gridTouch"
      :show-row-lines="true"
      :height="embedded ? '100%' : undefined"
      :error-row-enabled="!embedded"
      @cell-prepared="cellPrepared"
      @toolbar-preparing="onToolbarPreparing($event)"
      :scrolling="scrolling"
    >
      <dx-group-panel :visible="false" />

      <dx-grouping :auto-expand-all="expanded" expandMode="rowClick" />
      <dx-paging :page-size="defaultPageSize" />
      <dx-pager
        :visible="!embedded"
        :show-page-size-selector="true"
        :allowed-page-sizes="pageSizes"
        :show-info="true"
        :info-text="'Seite {0} von {1} ({2} Verkäufe)'"
      />
      <dx-filter-row :visible="!embedded" />
      <dx-load-panel :enabled="!embedded" />
      <dx-export
        :enabled="false"
        :allow-export-selected-data="true"
        file-name="Monatsabschluss"
        :texts="{
          exportAll: 'Alles herunterladen',
          exportSelectedRows: 'Auswahl herunterladen',
          exportTo: 'Excel Download',
        }"
      />

      <dx-selection :mode="embedded ? 'none' : 'multiple'" />

      <!-- nur Bildschirm (eingebettet): Gruppenzeile nur mit dem Wert, Kopf der Tarifstufe; Excel unverändert -->
      <template #gruppeWert="{ data }">
        <span>{{ data.value }}</span>
      </template>
      <template #ticketTarifKopf>
        <span>Ticket/Tarifstufe</span>
      </template>
    </dx-data-grid>
  </v-container>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxFilterRow,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxSelection,
  DxLoadPanel,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import moment from "moment";
import DataGridMixin from "@/mixins/DataGridMixin";

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxFilterRow,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxSelection,
    DxLoadPanel,
  },
  mixins: [DataGridMixin],
  props: {
    // eingebettet in die Seite Exporte: ohne eigene Leiste, Filterzeile, Pager, Ladeanzeige und Fehlerzeile
    embedded: { type: Boolean, default: false },
    // Berichtsmonat 'YYYY-MM' der Seite Exporte
    reportMonth: { type: String, default: null },
    // Start-Gruppierung (null | 'price' | 'line')
    initialGrouping: { type: String, default: null },
  },
  computed: {
    monthFormatted() {
      return moment(this.month).format("MM.YYYY");
    },
    dataUrl() {
      let option = "monthlyClosing";
      if (this.groupSelection == "price") {
        option = "monthlyClosingByPriceLevel";
      } else if (this.groupSelection == "line") {
        option = "monthlyClosingByLine";
      }
      return `${this.$store.state.baseUrl}ticketsale?customer_id=${
        this.$store.state.customerId
      }&sale_month=${this.month.substring(5, 7)}&sale_year=${this.month.substring(
        0,
        4
      )}&option=${option}`;
    },
  },
  watch: {
    dataUrl: function () {
      this.refreshDataGrid();
    },
    reportMonth(value) {
      if (value && value !== this.month) {
        this.month = value;
      }
    },
  },
  data() {
    return {
      month: this.reportMonth || new Date().toISOString().substr(0, 7),
      menu: false,
      expanded: false,
      groupSelection: this.initialGrouping || null,
      searchText: "",
      groupingOptions: [
        { value: null, text: 'Gruppierung nach Tarifstufe' },
        { value: 'price', text: 'Gruppierung nach Preisstufe' },
        { value: 'line', text: 'Gruppierung nach Linie' }
      ],
      gridRefName: "monthlyClosingDataGrid",
      datasource: new CustomStore({
        load: () => axios.get(this.dataUrl),
      }),
      summary: {
        totalItems: [
          {
            column: "effective_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
          },
          {
            column: "cancel_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
          },
          {
            column: "sale_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
          },
          {
            column: "passenger_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
          },
          {
            column: "all_price",
            summaryType: "sum",
            valueFormat: "#######0.00 €",
            displayFormat: "∑ {0}",
          },
        ],
        groupItems: [
          {
            column: "effective_count",
            summaryType: "sum",
            displayFormat: "{0} Tickets",
            showInGroupFooter: false,
            alignByColumn: false,
          },
          {
            column: "effective_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
            showInGroupFooter: true,
            alignByColumn: false,
          },
          {
            column: "cancel_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
            showInGroupFooter: true,
            alignByColumn: false,
          },
          {
            column: "sale_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
            showInGroupFooter: true,
            alignByColumn: false,
          },
          {
            column: "passenger_count",
            summaryType: "sum",
            displayFormat: "∑ {0}",
            showInGroupFooter: true,
            alignByColumn: false,
          },
          {
            column: "all_price",
            summaryType: "sum",
            valueFormat: "#######0.00 €",
            displayFormat: "∑ {0}",
            showInGroupFooter: true,
          },
        ],
      },
      columns: [
        {
          dataField: "line_id",
          caption: "Linie",
          alignment: "left",
          visible: false,
          ...(this.embedded ? { groupCellTemplate: "gruppeWert" } : {}),
        },
        {
          dataField: "ticket_name",
          caption: "Ticket",
          alignment: "left",
          groupIndex: "0",
          ...(this.embedded ? { groupCellTemplate: "gruppeWert" } : {}),
        },
        {
          dataField: "tariff_name",
          caption: "Tarifstufe",
          alignment: "left",
          ...(this.embedded ? { headerCellTemplate: "ticketTarifKopf" } : {}),
        },
        {
          dataField: "price_level",
          caption: "Preisstufe",
          alignment: "left",
        },
        {
          dataField: "tariff_zone",
          caption: "Tarifzonen",
          alignment: "left",
        },
        {
          dataField: "price",
          caption: "Einzelpreis",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
        },
        {
          dataField: "sale_count",
          caption: "Verkäufe",
          alignment: "left",
          cssClass: "app-zahl",
        },
        {
          dataField: "cancel_count",
          caption: "Stornierungen",
          alignment: "left",
          cssClass: "app-zahl",
        },
        {
          dataField: "passenger_count",
          caption: "Fahrgäste",
          alignment: "left",
          cssClass: "app-zahl",
        },
        {
          dataField: "effective_count",
          caption: "Gesamt",
          alignment: "left",
          cssClass: "app-zahl",
        },
        {
          dataField: "all_price",
          caption: "Einnahmen",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
        },
      ],
      onToolbarPreparing() {
        // Buttons are now in the filter row, no toolbar buttons needed
      },
      cellPrepared(e) {
        if (e.rowType === "data") {
          if (e.data.datum < new Date(2019, 11, 12)) {
            e.cellElement.style.color = "#AAAAAA";
          }
          if (e.data.betrag > 5) {
            if (e.column.dataField === "sale_id") {
              e.cellElement.style.fontWeight = "bold";
            }
            if (e.column.dataField === "price") {
              e.cellElement.style.backgroundColor = "#FFBB00";
              e.cellElement.style.color = "#000000";
            }
          }
        }

        if (e.rowType === "groupFooter" && e.column.dataField === "betrag") {
          e.cellElement.style.fontStyle = "italic";
        }
      },
    };
  },
  mounted() {
    if (this.groupSelection) {
      this.groupChanged({ value: this.groupSelection });
    }
  },
  methods: {
    refreshDataGrid() {
      const grid = this.$refs[this.gridRefName];
      if (grid && grid.instance) {
        grid.instance.refresh();
      }
    },
    onSearchInput() {
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.searchByText(this.searchText || '');
      }
    },
    onGroupingChange() {
      this.groupChanged({ value: this.groupSelection });
    },
    collapseAllClick() {
      this.expanded = !this.expanded;
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    groupChanged(e) {
      this.$refs[this.gridRefName].instance.clearGrouping();
      this.$refs[this.gridRefName].instance.clearSorting();

      switch (e.value) {
        case null:
          this.groupSelection = null;
          this.$refs[this.gridRefName].instance.columnOption(
            "ticket_name",
            "groupIndex",
            0
          );
          break;
        case "price":
          this.groupSelection = "price";
          this.$refs[this.gridRefName].instance.columnOption(
            "ticket_name",
            "groupIndex",
            0
          );
          break;
        case "line":
          this.groupSelection = "line";
          this.$refs[this.gridRefName].instance.columnOption("line_id", "groupIndex", 0);
          this.$refs[this.gridRefName].instance.columnOption(
            "ticket_name",
            "groupIndex",
            1
          );

          break;
        default:
      }
    },
  },
};
</script>

<style scoped>
/* eingebettet (Exporte): Flex-Spalte mit voller Höhe, das Grid füllt sie */
.export-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
}
</style>
